import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, Bell, Calendar, FileText, BookOpen } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { apiCall } from "../../lib/api";

export const GlobalSearch = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({
    staff: [],
    announcements: [],
    events: [],
    pages: []
  });
  const [loading, setLoading] = useState(false);

  // Static pages for search
  const staticPages = [
    { title: "Home", path: "/", category: "Pages" },
    { title: "Announcements", path: "/announcements", category: "Pages" },
    { title: "Events", path: "/events", category: "Pages" },
    { title: "Staff Directory", path: "/staff", category: "Pages" },
    { title: "Resources", path: "/resources", category: "Pages" },
    { title: "Contact Us", path: "/contact", category: "Pages" },
    { title: "Gallery", path: "/gallery", category: "Pages" },
    { title: "About Us", path: "/about", category: "Pages" },
    { title: "Student Portal", path: "/portal?type=student", category: "Portal" },
    { title: "Parent Portal", path: "/portal?type=parent", category: "Portal" },
    { title: "Teacher Portal", path: "/portal?type=teacher", category: "Portal" },
    { title: "Admin Portal", path: "/portal?type=admin", category: "Portal" },
    { title: "Student Handbook", path: "/resources/student-handbook", category: "Resources" },
    { title: "Bus Routes", path: "/resources/bus-routes", category: "Resources" },
    { title: "Lunch Menu", path: "/resources/lunch-menu", category: "Resources" },
    { title: "Enrollment Application", path: "/resources/enrollment-application", category: "Resources" },
    { title: "Pay Fees", path: "/fees", category: "Portal" },
  ];

  // Static staff data
  const staffData = [
    { id: 1, name: "[Staff Name]", title: "Principal", department: "Administration" },
    { id: 2, name: "[Staff Name]", title: "Vice Principal", department: "Administration" },
    { id: 3, name: "[Staff Name]", title: "Physics Teacher", department: "Science" },
    { id: 4, name: "[Staff Name]", title: "History Teacher", department: "Social Studies" },
    { id: 5, name: "[Staff Name]", title: "Spanish Teacher", department: "Languages" },
    { id: 6, name: "[Staff Name]", title: "Computer Science", department: "Technology" },
    { id: 7, name: "[Staff Name]", title: "Art Teacher", department: "Arts" },
    { id: 8, name: "[Staff Name]", title: "PE Director", department: "Athletics" },
    { id: 9, name: "[Staff Name]", title: "Librarian", department: "Library" },
    { id: 10, name: "[Staff Name]", title: "Math Teacher", department: "Mathematics" },
  ];

  useEffect(() => {
    if (!query.trim()) {
      setResults({ staff: [], announcements: [], events: [], pages: [] });
      return;
    }

    const searchTimeout = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    const lowerQuery = searchQuery.toLowerCase();

    try {
      // Search announcements and events from API
      const [announcementsRes, eventsRes] = await Promise.all([
        apiCall("GET", "/announcements").catch(() => []),
        apiCall("GET", "/events").catch(() => [])
      ]);

      // Filter staff
      const filteredStaff = staffData.filter(
        s => s.name.toLowerCase().includes(lowerQuery) ||
             s.title.toLowerCase().includes(lowerQuery) ||
             s.department.toLowerCase().includes(lowerQuery)
      );

      // Filter announcements
      const filteredAnnouncements = announcementsRes.filter(
        a => a.title?.toLowerCase().includes(lowerQuery) ||
             a.content?.toLowerCase().includes(lowerQuery)
      );

      // Filter events
      const filteredEvents = eventsRes.filter(
        e => e.title?.toLowerCase().includes(lowerQuery) ||
             e.description?.toLowerCase().includes(lowerQuery) ||
             e.location?.toLowerCase().includes(lowerQuery)
      );

      // Filter pages
      const filteredPages = staticPages.filter(
        p => p.title.toLowerCase().includes(lowerQuery) ||
             p.category.toLowerCase().includes(lowerQuery)
      );

      setResults({
        staff: filteredStaff.slice(0, 5),
        announcements: filteredAnnouncements.slice(0, 5),
        events: filteredEvents.slice(0, 5),
        pages: filteredPages.slice(0, 8)
      });
    } catch (err) {
      console.error("Search error:", err);
    }
    setLoading(false);
  };

  const handleResultClick = (path) => {
    navigate(path);
    onClose();
    setQuery("");
  };

  const totalResults = results.staff.length + results.announcements.length + results.events.length + results.pages.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search size={20} /> Search YCP School
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search staff, announcements, events, pages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 pr-10 py-6 text-lg"
            autoFocus
            data-testid="global-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {query && (
          <ScrollArea className="h-[400px] mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner"></div>
              </div>
            ) : totalResults === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No results found for "{query}"
              </div>
            ) : (
              <div className="space-y-6">
                {/* Pages Results */}
                {results.pages.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                      <FileText size={16} /> Pages & Links
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {results.pages.map((page, index) => (
                        <Card
                          key={index}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleResultClick(page.path)}
                          data-testid={`search-result-page-${index}`}
                        >
                          <CardContent className="p-3 flex items-center justify-between">
                            <span className="font-medium text-[#002147]">{page.title}</span>
                            <Badge variant="outline" className="text-xs">{page.category}</Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Staff Results */}
                {results.staff.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                      <Users size={16} /> Staff ({results.staff.length})
                    </h3>
                    <div className="space-y-2">
                      {results.staff.map((staff) => (
                        <Card
                          key={staff.id}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleResultClick("/staff")}
                          data-testid={`search-result-staff-${staff.id}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-[#002147]">{staff.name}</p>
                                <p className="text-sm text-gray-500">{staff.title}</p>
                              </div>
                              <Badge variant="secondary">{staff.department}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Announcements Results */}
                {results.announcements.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                      <Bell size={16} /> Announcements ({results.announcements.length})
                    </h3>
                    <div className="space-y-2">
                      {results.announcements.map((announcement, index) => (
                        <Card
                          key={announcement.id || index}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleResultClick("/announcements")}
                          data-testid={`search-result-announcement-${index}`}
                        >
                          <CardContent className="p-3">
                            <p className="font-medium text-[#002147]">{announcement.title}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{announcement.content}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Events Results */}
                {results.events.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                      <Calendar size={16} /> Events ({results.events.length})
                    </h3>
                    <div className="space-y-2">
                      {results.events.map((event, index) => (
                        <Card
                          key={event.id || index}
                          className="cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleResultClick("/events")}
                          data-testid={`search-result-event-${index}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-[#002147]">{event.title}</p>
                                <p className="text-sm text-gray-500">{event.location}</p>
                              </div>
                              <Badge variant="outline">{event.category}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        )}

        {!query && (
          <div className="py-8 text-center text-gray-500">
            <Search size={48} className="mx-auto mb-4 text-gray-300" />
            <p>Start typing to search across the entire school platform</p>
            <p className="text-sm mt-2">Search staff, announcements, events, and pages</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
